import type { DbClient } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { notFound, redirect } from "@tanstack/react-router";
import { createIsomorphicFn } from "@tanstack/react-start";
import {
    DogSex,
    getLifeStageLabel,
    type WagsListingBatchRecord,
} from "@wags/catalog/pure.ts";
import { ensureExperimentArms } from "@/lib/core/experiments/experiment-arm";
import { preloadSavedListingIdsOnClient } from "@/features/saved-listings";
import {
    type CollectionWagsListing,
    formatBreedNames,
    getDraftListingPreview,
    getListingDetail,
    getListingSharePreview,
    getWagsListingsCollection,
    prefetchWagsListings,
    toAdoptionStatus,
} from "@/features/listings";
import type { SharePreviewMedia } from "@wags/catalog/share-preview/share-preview.contract.ts";
import { seoMeta } from "@/lib/ui/seo";
import { dogListingShareMedia } from "./dog-share-media";

type DogListing = CollectionWagsListing & { ogImageUrl?: string | null };
type DogPageMode = "listing" | "draft";

export function dogCopy(dog: {
    title: string | null;
    description: string | null;
}) {
    return {
        dogName: dog.title?.trim() || "Puppy",
        dogDescription: dog.description?.trim() || "Description coming soon.",
    };
}

function getDogAgeLabel(detail: WagsListingBatchRecord) {
    if (detail.ageLabel) return detail.ageLabel;
    if (detail.organization?.type !== "shelter_rescue" || detail.dob == null)
        return undefined;
    return getLifeStageLabel(detail.dob) ?? undefined;
}

function getDogSex(detail: WagsListingBatchRecord) {
    if (
        detail.numberOfFemalesAvailable > 0 &&
        detail.numberOfMalesAvailable === 0
    )
        return DogSex.Female;
    if (
        detail.numberOfMalesAvailable > 0 &&
        detail.numberOfFemalesAvailable === 0
    )
        return DogSex.Male;
    return null;
}

function toDogListing(detail: WagsListingBatchRecord): DogListing {
    const birthday = detail.dob == null ? undefined : new Date(detail.dob);
    const imageUrls = detail.media.flatMap(({ url }) => (url ? [url] : []));

    return {
        ...detail,
        status: toAdoptionStatus(detail.status),
        organization: detail.organization
            ? {
                  ...detail.organization,
                  media: detail.organization.media.map((item) => ({
                      ...item,
                      url: item.url ?? null,
                  })),
              }
            : null,
        media: detail.media.map((item) => ({
            ...item,
            url: item.url ?? null,
        })),
        name: detail.title?.trim() || detail.breeds[0]?.name?.trim() || "Dog",
        location: detail.organization?.location ?? null,
        birthday,
        ageLabel: getDogAgeLabel(detail),
        listingBreedNames: detail.breedNames,
        weightBucket: detail.weightBucket,
        weightMinLbs: detail.weightMinLbs,
        weightMaxLbs: detail.weightMaxLbs,
        sex: getDogSex(detail),
        imageUrls,
        availableAt:
            detail.availableAt == null
                ? detail.createdAt
                : new Date(detail.availableAt),
        distanceMiles: null,
        summaryHydratedAt: Date.now(),
        detailHydratedAt: Date.now(),
    };
}

const loadListing = createIsomorphicFn()
    .server(
        async ({ listingId }: { dbClient: DbClient; listingId: string }) => {
            const listing = await getListingDetail({ data: { listingId } });
            if (!listing) throw notFound();
            return toDogListing(listing);
        },
    )
    .client(async ({ dbClient, listingId }) => {
        void preloadSavedListingIdsOnClient(dbClient);
        await prefetchWagsListings(dbClient, [listingId]);
        const listing = getWagsListingsCollection(dbClient).get(listingId);
        if (!listing) throw notFound();
        return listing;
    });

const loadSharePreview = createIsomorphicFn()
    .server(async (listingId: string) => getListingSharePreview(listingId))
    .client(
        async (_listingId: string): Promise<SharePreviewMedia | null> => null,
    );

const markDraftResponse = createIsomorphicFn()
    .server(async () => {
        const { setResponseHeaders } =
            await import("@tanstack/react-start/server");
        setResponseHeaders(
            new Headers({
                "Cache-Control": "private, no-store",
                "X-Robots-Tag": "noindex, nofollow",
            }),
        );
    })
    .client(() => {});

const loadDogRoute = async ({
    context,
    params,
}: {
    context: { dbClient: DbClient; queryClient: QueryClient };
    params: { dogId: string };
}) => {
    const [listing, sharePreview] = await Promise.all([
        loadListing({
            dbClient: context.dbClient,
            listingId: params.dogId,
        }),
        loadSharePreview(params.dogId),
        ensureExperimentArms(context.queryClient),
    ]);
    return listing && sharePreview ? { ...listing, sharePreview } : listing;
};

function dogRouteHead(mode: DogPageMode, titlePrefix?: string) {
    return ({
        loaderData,
        params,
    }: {
        loaderData?: Awaited<ReturnType<typeof loadListing>> & {
            sharePreview?: SharePreviewMedia;
        };
        params: { dogId: string };
    }) => {
        const dogName = loaderData?.title?.trim() || "Dog";
        const breed = loaderData && formatBreedNames(loaderData);
        const location = [
            loaderData?.organization?.city,
            loaderData?.organization?.state,
        ]
            .filter(Boolean)
            .join(", ");
        const details = [breed, location].filter(Boolean).join(" in ");
        const description = loaderData?.description?.trim()
            ? loaderData.description.trim().slice(0, 155)
            : `Meet ${dogName}${details ? `, a ${details}` : ""}. View photos and adoption details on Wags.`;
        const shareMedia = dogListingShareMedia(loaderData);

        return seoMeta({
            title: titlePrefix
                ? `${titlePrefix} ${dogName} | Wags`
                : `${dogName}${details ? ` - ${details}` : ""} | Wags`,
            description,
            url: `/dog/${params.dogId}${mode === "draft" ? "/draft" : ""}`,
            image: shareMedia.url,
            imageMimeType: shareMedia.mimeType,
            video: shareMedia.video,
            type: "website",
            noindex: mode === "draft" || loaderData?.status === "unavailable",
            nofollow: mode === "draft",
        });
    };
}

export const catalogDogRoute = {
    loader: loadDogRoute,
    head: dogRouteHead("listing"),
};

export const catalogDogAboutRoute = {
    loader: async (input: Parameters<typeof loadDogRoute>[0]) => {
        const listing = await loadDogRoute(input);
        if (listing.status === "unavailable") {
            throw redirect({
                to: "/dog/$dogId",
                params: input.params,
                replace: true,
            });
        }
        return listing;
    },
    head: dogRouteHead("listing", "About"),
};

export const catalogDraftDogRoute = {
    beforeLoad: markDraftResponse,
    staleTime: 0,
    loader: async ({ params }: { params: { dogId: string } }) => {
        const preview = await getDraftListingPreview({
            data: { listingId: params.dogId },
        });
        if (!preview) throw notFound();
        return { ...toDogListing(preview.listing), draftPage: preview.page };
    },
    head: dogRouteHead("draft"),
};
